import PrintBlock from "@/v2/components/print/block/base/Block";
import BlockHeading from "@/v2/components/print/block/base/Heading";
import BlockRowContent from "@/v2/components/print/block/base/RowContent";
import GrandTotalBlock from "@/v2/components/print/block/presets/GrandTotal";
import TotalAmountInWords from "@/v2/components/print/block/presets/TotalAmountInWords";
import { color } from "@/v2/components/print/common";
import PrintNewPage from "@/v2/components/print/common/NewPage";
import PrintTitle from "@/v2/components/print/common/Title";
import { Payslip } from "@/v2/types";
import { Document, Text, View } from "@react-pdf/renderer";

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function ArOfficeStaffPayslipPDF({ data }: { data: Payslip }) {
  const {
    employee_name,
    trade,
    iqama_number,
    basic_salary,
    commission,
    absent_deduction,
    advance_or_other_deduction,
    balance_to_deduct,
    food_allowance,
    net_amount,
    month,
    year,
    bonus,
    note,
  } = {
    employee_name: data.employee_name,
    trade: data.employee.trade,
    iqama_number: data.employee.iqama,
    basic_salary: data.basic_salary,
    commission: data.commission,
    absent_deduction: data.absent_deduction,
    advance_or_other_deduction: data.advance_or_other_deduction,
    balance_to_deduct: data.balance_to_deduct,
    food_allowance: data.food_allowance,
    net_amount: data.net_amount,
    month: MONTH_OPTIONS[data.month - 1],
    year: data.year,
    bonus: data?.eid_bonus,
    note: data?.note,
  };

  return (
    <Document>
      <PrintNewPage>
        <PrintTitle englishTitle="PAYSLIP" arabicTitle="قسيمة الراتب" />
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <PrintBlock
            style={{
              flex: 1,
              backgroundColor: color.bg,
              marginLeft: 0,
              padding: "10px",
              borderRadius: "4px",
            }}
          >
            <BlockHeading
              englishTitle="Employee Details"
              arabicTitle="بيانات الموظف"
            />

            <BlockRowContent
              englishLabel="Employee Name"
              arabicLabel="اسم الموظف"
              content={employee_name || "N/A"}
              boldContent
              disableArabicContent
            />

            <BlockRowContent
              disableArabicContent
              englishLabel="Trade"
              arabicLabel="المهنة"
              content={trade[0] || "N/A"}
            />

            <BlockRowContent
              englishLabel="Iqama No"
              arabicLabel="رقم الإقامة"
              content={iqama_number || "N/A"}
              disableArabicContent
            />
            <BlockRowContent
              englishLabel="Month"
              arabicLabel="الشهر"
              content={`${month}-${year}` || "N/A"}
              disableArabicContent
            />
          </PrintBlock>
          <PrintBlock
            style={{
              flex: "1",
              backgroundColor: color.bg,
              marginLeft: "2px",
              padding: "10px",
              borderRadius: "4px",
            }}
          >
            <BlockHeading
              englishTitle="Salary Summary"
              arabicTitle="ملخص الراتب"
            />

            <BlockRowContent
              englishLabel="Basic Salary"
              arabicLabel="الراتب الأساسي"
              disableArabicContent
              content={`${String(basic_salary)} SR` || "0.00"}
            />

            <BlockRowContent
              englishLabel="Commission"
              arabicLabel="العمولة"
              content={`${String(commission)} SR` || "0.00"}
              disableArabicContent
            />

            <BlockRowContent
              disableArabicContent
              englishLabel="Food Allowance"
              arabicLabel="بدل الطعام"
              content={`${String(food_allowance)} SR` || "0.00"}
            />

            {bonus > 0 && (
              <BlockRowContent
                disableArabicContent
                englishLabel="Bonus"
                arabicLabel="مكافأة"
                content={`${String(bonus)} SR` || "0.00"}
              />
            )}

            <BlockRowContent
              disableArabicContent
              englishLabel="Absent Deduction"
              arabicLabel="خصم الغياب"
              content={`${String(absent_deduction)} SR` || "0.00"}
              style={{
                color: "red",
              }}
            />

            <BlockRowContent
              disableArabicContent
              englishLabel="Advance / Other Deduction"
              arabicLabel="السلفة / خصم آخر"
              content={
                `${String(advance_or_other_deduction)} SR` ||
                "0.00"
              }
              style={{
                color: "red",
              }}
            />

            <BlockRowContent
              disableArabicContent
              englishLabel="Balance To Deduct"
              arabicLabel="الرصيد المستقطع"
              content={
                `${String(balance_to_deduct)} SR` || "0.00"
              }
              style={{
                color: "#7d2f25",
              }}
            />
          </PrintBlock>
        </View>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
          }}
        >
          {/* Left box - Note */}
          <View
            style={{
              width: "50%",
              flexShrink: 0,
              padding: 10,
            }}
          >
            <View
              style={{
                width: "100%",
                display: "flex",
              }}
            >
              <PrintBlock>
                <BlockHeading
                  englishTitle="Note"
                  arabicTitle="ملاحظة"
                />
              </PrintBlock>
              <View>
                {note ? (
                  <Text
                    style={{
                      margin: "2px 0px",
                    }}
                  >
                    {note}
                  </Text>
                ) : (
                  <Text
                    style={{
                      fontSize: 6,
                      margin: "2px 0px",
                    }}
                  >
                    No additional notes provided
                  </Text>
                )}
              </View>
            </View>
          </View>

          {/* Right box - Total */}
          <View
            style={{
              width: "50%",
              flexShrink: 0,
              padding: 10,
            }}
          >
            <PrintBlock>
              <BlockHeading
                englishTitle="Financial Summary"
                arabicTitle="الملخص المالي"
              />
            </PrintBlock>
            <GrandTotalBlock
              englishLabel="Net Amount"
              arabicLabel="صافي المبلغ"
              amount={net_amount}
            />

            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <TotalAmountInWords
                grandTotal={Number(net_amount)}
              />
            </View>
          </View>
        </View>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            marginTop: 70,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text>Prepared By _______________</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text>Received By _______________</Text>
          </View>
        </View>
      </PrintNewPage>
    </Document>
  );
}
